/**
 * Global configuration for the frontend application
 */
import axios from 'axios';

// Environment-specific configuration
const ENV = process.env.REACT_APP_ENV || 'development';
// Avoid double /api in URLs by checking if the URL already includes /api
let API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// If the URL doesn't end with /api, add it
if (!API_BASE_URL.endsWith('/api')) {
  API_BASE_URL = `${API_BASE_URL}/api`;
}

// API configuration
const API_CONFIG = {
  BASE_URL: API_BASE_URL,
  AUTH_URL: `${API_BASE_URL}/auth`,
  UPLOAD_URL: `${API_BASE_URL}/uploads`,
  ENV: ENV
};

// Configure axios defaults
export const configureAxios = () => {
  // Set default base URL
  axios.defaults.baseURL = API_CONFIG.BASE_URL;
  
  // Set withCredentials based on environment
  axios.defaults.withCredentials = true;
  
  // Add request interceptor to handle errors consistently
  axios.interceptors.request.use(
    config => {
      // Add timestamp to prevent caching (if needed)
      if (config.method === 'get') {
        config.params = {
          ...config.params,
          _t: Date.now()
        };
      }
      
      // Log request in development
      if (ENV === 'development') {
        console.log(`Making ${config.method.toUpperCase()} request to: ${config.url}`);
      }

      return config;
    },
    error => Promise.reject(error)
  );
  
  // Add response interceptor to handle common error scenarios
  axios.interceptors.response.use(
    response => response,
    error => {
      // Log error details in development
      if (ENV === 'development') {
        console.error('API Error:', error);
      }

      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || 'An error occurred';

        switch (status) {
          case 401:
            console.error('Unauthorized access - you may need to login again');
            // Could trigger logout here if needed
            break;
          case 403:
            console.error('Forbidden - you do not have permission to access this resource');
            break;
          case 404:
            console.error('Resource not found');
            break;
          case 500:
            console.error('Server error:', message);
            break;
          default:
            console.error(`Error ${status}:`, message);
        }
      } else if (error.request) {
        console.error('Network error: Server may be down or unreachable');
      } else {
        console.error('Request setup error:', error.message);
      }
      return Promise.reject(error);
    }
  );
  
  // Log configuration in development
  if (ENV === 'development') {
    console.log('API Configuration:', {
      env: ENV,
      baseUrl: API_CONFIG.BASE_URL,
      authUrl: API_CONFIG.AUTH_URL
    });
  }
};

export default API_CONFIG; 